import phoneCountryCodes from '../assets/phone-country-codes.json';

interface CountryCodeData {
  code: string;
  country: string;
  flag: string;
  name: string;
}

// Build a lookup map for O(1) access
const countryCodeMap = new Map<string, CountryCodeData>(
  (phoneCountryCodes as CountryCodeData[]).map((c) => [
    c.country.toUpperCase(),
    c,
  ]),
);

/**
 * Get country information by ISO 2-letter country code
 */
export function getCountryInfo(
  countryCode: string,
): CountryCodeData | undefined {
  return countryCodeMap.get(countryCode.toUpperCase());
}

/**
 * Get country flag emoji by ISO 2-letter country code
 * Falls back to generating flag from regional indicator symbols if not in our data
 */
export function getCountryFlag(countryCode: string): string {
  const info = getCountryInfo(countryCode);
  if (info) return info.flag;

  // Fallback: generate flag emoji from regional indicator symbols
  // Each letter is offset by 0x1F1E6 - 0x41 = 127397 from ASCII
  const code = countryCode.toUpperCase();
  if (code.length === 2 && /^[A-Z]{2}$/.test(code)) {
    return String.fromCodePoint(
      ...code.split('').map((c) => c.charCodeAt(0) + 127397),
    );
  }

  return '';
}

/**
 * Get country name by ISO 2-letter country code
 * Falls back to Intl.DisplayNames API if not in our data
 */
export function getCountryName(countryCode: string): string {
  const info = getCountryInfo(countryCode);
  if (info) return info.name;

  // Fallback: use Intl.DisplayNames API
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    const name = displayNames.of(countryCode.toUpperCase());
    if (name) return name;
  } catch {
    // Ignore errors, fall back to code
  }

  return countryCode.toUpperCase();
}

/**
 * Format a country code as "🇺🇸 United States"
 * Used for displaying countries in filters and charts
 */
export function formatCountryLabel(countryCode: string): string {
  const flag = getCountryFlag(countryCode);
  const name = getCountryName(countryCode);

  if (flag) {
    return `${flag} ${name}`;
  }

  return name;
}
